import { useEffect, useState } from "react";
import { usePaged, Pager, SearchBar } from "@/components/fo/Pager";
import { toast } from "sonner";
import { Crown, Play, RotateCcw } from "lucide-react";
import { api, errMsg, fmtDate, fmtTime } from "@/lib/api";

const STATUS = { sent: "text-[#00F5D4]", skipped: "text-slate-500", failed: "text-[#FF2E63]", opted_out: "text-slate-500" };

const EMPTY = [];

export const EmailsAdmin = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const failed = (d?.logs || EMPTY).filter((l) => l.status === "failed").length;
  const retry = async () => {
    setBusy("retry");
    try {
      const { data } = await api.post("/admin/emails/retry");
      toast.success(`Retrying ${data.queued} emails`, { description: "They go out one by one — refresh in a minute to see the result." });
      setTimeout(() => { load(); setBusy(""); }, Math.min(60000, 1500 + data.queued * 1200));
    } catch { toast.error("Retry failed"); setBusy(""); }
  };
  const logs = usePaged(d?.logs || EMPTY, { query: q, fields: ["to", "subject", "kind", "status"] });
  const load = () => api.get("/admin/emails").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const run = async (name) => {
    setBusy(name);
    try { const { data } = await api.post(`/admin/jobs/${name}`); toast.success(data.status === "already_done" ? "Already ran for this period" : `Done — ${data.emails} emails`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!d) return null;
  return (
    <div className="space-y-6" data-testid="admin-emails">
      <div className="grid gap-4 md:grid-cols-3">
        <div className="panel p-5">
          <div className="label-mono mb-2">Daily job · 07:00</div>
          <p className="mb-4 text-sm text-slate-400">Books guaranteed plays for the day, emails each artist their times + story card, picks Artist of the Month on the 1st, sends plan-expiry reminders.</p>
          <button className="btn-ghost !py-2 text-sm" onClick={() => run("daily")} disabled={!!busy} data-testid="run-daily-job"><Play className="h-4 w-4" />{busy === "daily" ? "Running…" : "Run now"}</button>
        </div>
        <div className="panel p-5">
          <div className="label-mono mb-2">Weekly job · Monday 08:00</div>
          <p className="mb-4 text-sm text-slate-400">Closes the Weekly Charts, assigns prizes (with prize emails) and sends every artist their weekly report.</p>
          <button className="btn-ghost !py-2 text-sm" onClick={() => run("weekly")} disabled={!!busy} data-testid="run-weekly-job"><Play className="h-4 w-4" />{busy === "weekly" ? "Running…" : "Run now"}</button>
        </div>
        <div className="panel p-5" data-testid="admin-aotm">
          <div className="label-mono mb-2 !text-[#FFB800]">Artist of the Month</div>
          {d.aotm?.artist_name ? <div className="flex items-center gap-3"><Crown className="h-6 w-6 text-[#FFB800]" /><div><div className="font-display text-xl font-bold">{d.aotm.artist_name}</div><div className="font-mono text-xs text-slate-500">{d.aotm.label} · prime-time until {fmtDate(d.aotm.until)}</div></div></div>
            : <p className="text-sm text-slate-400">Chosen automatically on the 1st of each month: the artist with the most weeks in a top 3. The magazine feature appears in Orders.</p>}
        </div>
      </div>
      <div className="panel p-5" data-testid="admin-slots">
        <div className="label-mono mb-3">Booked plays (guaranteed slots)</div>
        {d.slots.length ? <div className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">{d.slots.map((s) => (
          <div key={s.id} className="flex justify-between border-b border-white/5 py-1.5 text-sm"><span className="truncate">{s.artist_name} — {s.title}</span><span className={`font-mono text-xs ${s.prime ? "text-[#FF2E63]" : "text-slate-500"}`}>{fmtTime(s.at)}{s.used ? " ✓" : ""}</span></div>
        ))}</div> : <p className="text-sm text-slate-500">No upcoming booked slots. Run the daily job.</p>}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SearchBar value={q} set={setQ} placeholder="Search recipient, subject, type, status…" id="emails" />
        <button className="btn-hot !px-4 !py-2 text-xs" disabled={!failed || !!busy} onClick={retry} data-testid="retry-failed-emails"><RotateCcw className="h-4 w-4" />{busy === "retry" ? "Retrying…" : `Retry failed (${failed})`}</button>
      </div>
      <div className="panel overflow-x-auto" data-testid="admin-email-log">
        <table className="w-full min-w-[640px] text-sm">
          <thead><tr className="border-b border-white/10 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">{["When", "Type", "To", "Subject", "Status"].map((h) => <th key={h} className="p-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>{logs.rows.map((l) => (
            <tr key={l.id} className="border-b border-white/5"><td className="p-3 font-mono text-xs text-slate-400">{fmtDate(l.created_at)} {fmtTime(l.created_at)}</td><td className="p-3 font-mono text-xs uppercase">{l.kind}</td><td className="p-3 text-xs">{l.to}</td><td className="p-3">{l.subject}</td><td className={`p-3 font-mono text-xs uppercase ${STATUS[l.status]}`} data-testid={`email-status-${l.id}`}>{l.status}{l.error && <div className="mt-1 max-w-[220px] break-words font-sans text-[10px] normal-case text-slate-500">{l.error}</div>}</td></tr>
          ))}</tbody>
        </table>
        {!logs.total && <p className="p-6 text-center text-slate-500">No emails yet.</p>}
      </div>
      <Pager {...logs} id="emails" />
      <p className="text-xs text-slate-500">Emails to demo accounts (@fuorionda.demo, @example.com) are logged as skipped and never sent.</p>
    </div>
  );
};
